import Match from '@src/models/Match'
import Odd from '@src/models/Odd'
import Period from '@src/models/Period'
import type { MetaData } from '@src/models/MetaData'
import { OfferStatus, OfferType } from '@src/models/enums'
import { stringToDate, parseTimeString } from '@src/helpers/helpers'
import { MatchResultMapper } from '@src/mappers/MatchResultMapper'
import {
    OddStatus,
    OfferStatusV3,
    OddStatusV3,
    OfferTypeV3
} from '@superbet-group/offer.clients.lib'
import type {
    EventDtoV3,
    FixtureDtoV3,
    OddDtoV3,
    MarketDtoV3,
    PeriodDtoV3,
} from '@superbet-group/offer.clients.lib'

const matchResultMapper = new MatchResultMapper()

// v3 -> v2 conversion
const OFFER_STATUS: Partial<Record<OfferStatusV3, OfferStatus>> = {
    [OfferStatusV3.ACTIVE]: OfferStatus.active,
    [OfferStatusV3.FINISHED]: OfferStatus.finished,
    [OfferStatusV3.STOPPED]: OfferStatus.stop
}
const ODD_STATUS: Record<OddStatusV3, string> = {
    [OddStatusV3.STOP]: OddStatus.stop,
    [OddStatusV3.ACTIVE]: OddStatus.active,
    [OddStatusV3.BLOCK]: OddStatus.block,
    [OddStatusV3.WIN]: OddStatus.win,
    [OddStatusV3.LOST]: OddStatus.lost,
    [OddStatusV3.REFUND]: OddStatus.refund
}


// build event from a v3 message or merge the message with the local event.
export function transformEventV3(dto: EventDtoV3, local?: Readonly<Match>): Match {
    // copy local event so it can be merged with v3 message.
    const e: Match = local ? Object.assign(new Match(), local) : new Match()

    e.id = dto.event_id
    e.incrementId = dto.increment_id

    if (dto.fixture) {
        applyFixture(e, dto.fixture, dto)
    }

    applyOdds(e, dto, !local)
    applyMetaData(e, dto)

    return e
}

function applyFixture(e: Match, fixture: FixtureDtoV3, dto: EventDtoV3) {
    e.uuid = fixture.uuid
    e.specialOfferId = null
    e.fullName = fixture.event_name
    e.sportId = fixture.sport_id
    e.categoryId = fixture.category_id
    e.tournamentId = fixture.tournament_id
    e.team1Id = parseInt(fixture.home_team_id, 10)
    e.team2Id = parseInt(fixture.away_team_id, 10)
    e.date = stringToDate(fixture.utc_date)
    e.code = fixture.event_code
    e.matchPromotedLive = fixture.event_promoted_in_play_order
    e.tournamentPromotedLive = fixture.tournament_promoted_in_play_order
    e.matchTags = fixture.event_tags ? fixture.event_tags.split(',') : []

    // need to check if state status is included as it can be omitted
    if (fixture.offer_state_status) {
        e.offerStatus = { ...e.offerStatus } as Record<OfferType, OfferStatus>
        Object.entries(fixture.offer_state_status).forEach(([offerType, status]) => {
            const mapped = OFFER_STATUS[status]

            if (mapped) {
                e.offerStatus[Number(offerType) as OfferType] = mapped
            }
        })
    }

    // if inplay stats are included, we can map match results. if not, we keep the local match results as they are.
    if (dto.inplay_stats) {
        e.matchResults = matchResultMapper.createTargetObject({
            matchResults: fixture.match_results,
            metaData: {
                homeTeamScore: dto.inplay_stats.home_team_score,
                awayTeamScore: dto.inplay_stats.away_team_score
            }
        })
    }
}

function applyOdds(e: Match, dto: EventDtoV3, isNew: boolean) {
    // full update / new event has full odds
    if (isNew || dto.is_full_market_update) {
        e.setOdds(liveOdds(dto.markets))
    } else if (dto.markets) {
        // partial update only carries changed odds
        e.setOdds(mergeOdds(e.getOdds(), dto.markets))
    } else {
        // as local event was frozen, we need to copy the odds
        // so denormalization can write to them
        // TODO: rework this logic when removing v2 support: 
        // include struct manager in mapping flow
        e.setOdds(e.getOdds().map((odd) => Object.assign(new Odd(), odd)))
    }

    if (isNew || dto.is_full_results_update) {
        e.setOddResults(resultOdds(dto.results))
    }
}

// get in-play, visible odds
function liveOdds(markets: MarketDtoV3[] = []): Odd[] {
    const odds: Odd[] = []

    markets.forEach((market) =>
        (market.odds || []).forEach((odd) => {
            if (odd.metadata?.offer_state_id === OfferTypeV3.INPLAY && odd.display !== false) {
                odds.push(buildOdd(new Odd(), odd, market))
            }
        })
    )

    return odds
}

function resultOdds(markets: MarketDtoV3[] = []): Odd[] {
    const odds: Odd[] = []

    markets.forEach((market) =>
        (market.odds || []).forEach((odd) => {
            if (odd.display !== false) {
                odds.push(buildOdd(new Odd(), odd, market))
            }
        })
    )

    return odds
}

// merge a partial odd update by uuid
function mergeOdds(localOdds: Odd[], markets: MarketDtoV3[]): Odd[] {
    // clone every local odd: the store froze them and denormalization writes to each one
    const byUuid = new Map<Odd['uuid'], Odd>(
        localOdds.map((odd) => [odd.uuid, Object.assign(new Odd(), odd)])
    )

    markets.forEach((market) =>
        (market.odds || []).forEach((oddDto: OddDtoV3) => {
            if (oddDto.display === false) {
                // remove displayed disabled odds
                byUuid.delete(oddDto.uuid)
            } else if (byUuid.has(oddDto.uuid)) {
                // update existing odd
                byUuid.set(oddDto.uuid, buildOdd(byUuid.get(oddDto.uuid)!, oddDto, market))
            } else if (oddDto.metadata) {
                // add new odd if it's inplay (extra guard)
                if (oddDto.metadata.offer_state_id === OfferTypeV3.INPLAY) {
                    byUuid.set(oddDto.uuid, buildOdd(new Odd(), oddDto, market))
                }
            }
        })
    )

    return [...byUuid.values()]
}

// uses only the fields the dto carries
function buildOdd(odd: Odd, dto: OddDtoV3, market: MarketDtoV3): Odd {
    odd.uuid = dto.uuid

    if (dto.price !== undefined) {
        odd.value = dto.price
    }

    if (dto.status !== undefined) {
        odd.status = ODD_STATUS[dto.status]
    }

    if (dto.metadata) {
        const m = dto.metadata

        odd.id = m.outcome_id
        odd.marketUuid = m.market_line_uuid
        odd.name = m.name
        odd.code = m.code
        odd.specialBetValue = m.special_bet_value
        odd.specifiers = m.specifiers
        odd.tags = m.tags
        odd.description = m.info
        odd.offerType = m.offer_state_id as unknown as OfferType
    }

    if (market.metadata) {
        odd.marketId = market.id
        odd.marketOrder = market.metadata.market_group_order
        odd.marketTag = market.metadata.tags
        odd.initialMarketName = market.name
    }

    return odd
}

// metadata is mostly always partial
// so just override just the fields present and keep local for the rest
function applyMetaData(e: Match, dto: EventDtoV3) {
    if (!dto.inplay_stats && !dto.inplay_stats_metadata) {
        return
    }

    e.metaData = { ...e.metaData, ...metaFromDto(dto) } as MetaData

    if (dto.inplay_stats_metadata) {
        const isLive = e.offerStatus?.[OfferType.live] === OfferStatus.active

        e.oddCount = isLive ? dto.inplay_stats_metadata.counts?.odds?.[OfferTypeV3.INPLAY] : 0
    }
}

function metaFromDto(dto: EventDtoV3): Partial<MetaData> {
    const meta: Partial<MetaData> = {}
    const stats = dto.inplay_stats
    const statsMeta = dto.inplay_stats_metadata

    if (stats) {
        if (stats.home_team_score !== undefined) meta.team1Score = parseInt(stats.home_team_score, 10)
        if (stats.away_team_score !== undefined) meta.team2Score = parseInt(stats.away_team_score, 10)
        if (stats.minutes !== undefined) meta.minute = Number(stats.minutes)
        if (stats.periods) meta.periods = stats.periods.map(toPeriod)
        if (stats.team_serving === 1 || stats.team_serving === 2) meta.serve = stats.team_serving
        if (stats.stoppage_time) meta.stoppageTime = parseTimeString(stats.stoppage_time)
        if (stats.home_team_yellow_cards !== undefined) meta.t1YCrd = stats.home_team_yellow_cards
        if (stats.away_team_yellow_cards !== undefined) meta.t2YCrd = stats.away_team_yellow_cards
        if (stats.home_team_red_cards !== undefined) meta.t1RCrd = stats.home_team_red_cards
        if (stats.away_team_red_cards !== undefined) meta.t2RCrd = stats.away_team_red_cards
        if (stats.home_team_corners !== undefined) meta.t1Crn = stats.home_team_corners
        if (stats.away_team_corners !== undefined) meta.t2Crn = stats.away_team_corners
    }

    if (statsMeta) {
        if (statsMeta.period_status !== undefined) meta.periodStatus = statsMeta.period_status
        if (statsMeta.status !== undefined) meta.status = statsMeta.status
    }

    return meta
}

function toPeriod(dto: PeriodDtoV3): Period {
    const period = new Period()

    period.team1Score = parseInt(dto.home_team_score, 10)
    period.team2Score = parseInt(dto.away_team_score, 10)

    return period
}
